<template>
	<TransitionGroup tag="ul" :name="props.transition" class="list avatar-list relative z-1" :class="{'flex-no-wrap': props.noWrap, 'flex-row-reverse': props.rtl}">
		<tooltip v-for="(model, i) in displayModels" :key="`${i}-${model.id}`" bottom :text="model.display_name"
				 tag="li"
				 :style="{borderColor: i === focusedIndex ? props.selectedColor ?? $config.public.colors.success : undefined,
				 'z-index': hoverIndex === i ? displayModels.length + 1 : (hoverIndex !== null && i > hoverIndex ? (displayModels.length - i) : displayModels.length - i)}"
				 @click="$emit('click', model)"
				 @mouseenter="hoverIndex = i"
				 @mouseleave="hoverIndex = focusedIndex">
			<nuxt-link v-if="props.showResolver" :to="props.showResolver(model)">
				<avatar :model="model" :size="size" :color="$config.public.colors.light_gray"/>
			</nuxt-link>
			<avatar v-else :model="model" :size="size" :color="$config.public.colors.light_gray"/>
		</tooltip>

		<li v-if="hiddenAmount > 0" :style="{'z-index': hoverIndex === -1 ? displayModels.length + 1 : 0}"
			key="show-all"
			@click="$emit('show-all')"
			@mouseenter="hoverIndex = -1"
			@mouseleave="hoverIndex = focusedIndex">
			<nuxt-link v-if="props.showAllResolver" :to="props.showAllResolver()" class="flex items-center justify-center relative rounded-full bg-gray-300 pr-2" :style="{width: `${size}px`, height: `${size}px`}">
				<span class="text-white" :style="{fontSize: `${Math.max(7, (size / 3.5))}px`}">+{{ hiddenAmount }}</span>
			</nuxt-link>
			<div v-else class="flex items-center justify-center relative rounded-full bg-gray-300 pr-2" :style="{width: `${size}px`, height: `${size}px`}">
				<span class="text-white" :style="{fontSize: `${Math.max(7, (size / 3.5))}px`}">+{{ hiddenAmount }}</span>
			</div>
		</li>
		<li v-else class="none" key="none"/>
	</TransitionGroup>
</template>

<script setup lang="ts">
import type {Model} from "@spoferan/spoferan-ts-core";
import {computed, watch, ref} from "vue";

const props = withDefaults(defineProps<{
	models: Model[],

	size?: number,
	
	// The max number of specified models to show before a "show all" button is shown
	max?: number,

	// A custom number of the total models in the list when not all models are specified in the models array
	total?: number,

	// Resolves the URL to redirect when a model is clicked
	showResolver?: (model: Model) => string,

	// Resolves the URL to redirect when the "show all" button is clicked
	showAllResolver?: () => string,

	// Whether a click on an avatar shall open the link in a new browser tab
	blank?: boolean,

	// The id of a focused model
	selected?: number|string,

	selectedColor?: string,

	// The transition to use when adding or removing avatars
	transition?: string,

	noWrap?: boolean,

	// By default, the avatars are displayed in a row with the first entry on the very left. If rtl is set to true, the first entry is on the very right.
	rtl?: boolean
}>(), {
	max: 5,
	size: 34,
});
const emit = defineEmits(['click', 'show-all']);

const displayModels = computed(() => {
	const validModels = props.models && props.models.length ? props.models.filter(model => !!model) : [];

	// +1 for the hidden amount "show all" button
	return props.max && (props.total ?? validModels.length) > props.max ? validModels.slice(0, props.max - 1) : validModels;
});

const hiddenAmount = computed(() => {
	if (props.total) {
		return props.total - displayModels.value.length;
	}

	return Math.max(0, props.models.length - displayModels.value.length);
});

const focusedIndex = computed(() => {
	if (props.selected) {
		const selectedValue = isNaN(props.selected) ? props.selected : parseInt(props.selected);
		const index = displayModels.value.findIndex(model => model.id === selectedValue);
		if (index > -1) {
			return index;
		}
	}
	return null;
});
watch(focusedIndex, (newValue) => {
	hoverIndex.value = newValue;
});

// The hover index to adapt the z-index on hovering
const hoverIndex = ref(focusedIndex.value);

</script>
<style>
.list.avatar-list {
  display: flex;
  flex-wrap: wrap;
}
.list.avatar-list:not(.flex-row-reverse) > *:not(:first-child) {
  margin-left: -8px;
}
.list.avatar-list.flex-row-reverse > *:not(:last-child) {
  margin-left: -8px;
}
.list.avatar-list > .none + * {
  margin-left: 0;
}
.list.avatar-list li, .list.avatar-list .list-item {
  padding: 0;
  margin-bottom: 3px;
  margin-top: 3px;
  border: 2px solid white;
  border-radius: 100%;
  overflow: hidden;
}
</style>